import {
  Paper,
  Group,
  Button,
  SegmentedControl,
  Badge,
  Box,
  Alert,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconPolygon,
  IconLineDotted,
  IconPoint,
  IconCheck,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  useEffect,
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Draw, Modify, Snap } from "ol/interaction";
import { WKT } from "ol/format";
import { Geometry } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import Feature from "ol/Feature";
import useTransientStore from "../../data/transcientStore";
import DrawingModes from "../../types/Drawing";
import { useTranslation } from "../../hooks/useTranslation";

interface DrawingControlsProps {
  onGeometryCaptured?: (wkt: string, drawingType: string) => void;
  showTileLayerSwitch?: boolean;
  tilelayer?: "osm" | "satellite";
  onTileLayerChange?: (layer: "osm" | "satellite") => void;
  compact?: boolean;
  onDrawingStart?: () => void;
  onDrawingStop?: () => void;
  mode?: "view" | "create";
  initialWkt?: string;
  instanceId?: string;
  disabled?: boolean;
}

export interface DrawingControlsRef {
  clearGeometry: () => void;
}

const DrawingControls = forwardRef<DrawingControlsRef, DrawingControlsProps>(
  (
    {
      onGeometryCaptured,
      showTileLayerSwitch = true,
      tilelayer = "osm",
      onTileLayerChange,
      compact = false,
      onDrawingStart,
      onDrawingStop,
      mode = "view",
      initialWkt,
      instanceId,
      disabled = false,
    },
    ref,
  ) => {
    const [geometryCaptured, setGeometryCaptured] = useState(false);
    const [geometryLoaded, setGeometryLoaded] = useState(false);
    const [loadedGeometryType, setLoadedGeometryType] = useState<string>("");
    const [capturedGeometryType, setCapturedGeometryType] =
      useState<string>("");

    // Use local state for drawing instead of global state
    const [localDrawing, setLocalDrawing] = useState(false);
    const [localDrawingMode, setLocalDrawingMode] = useState<DrawingModes>(
      DrawingModes.Polygon,
    );

    const map = useTransientStore((state) => state.map);
    const selectedAddressSystem = useTransientStore(
      (state) => state.selectedAddressSystem,
    );
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({
      pageId: 325,
      addressSystemId: addrIdFromStore || 0,
      autoLoad: true,
    });
    const shouldClearDrawing = useTransientStore(
      (state) => state.shouldClearDrawing,
    );
    const excludeInstanceId = useTransientStore(
      (state) => state.excludeInstanceId,
    );
    const resetClearDrawing = useTransientStore(
      (state) => state.resetClearDrawing,
    );
    const triggerClearDrawing = useTransientStore(
      (state) => state.triggerClearDrawing,
    );

    // Use local state directly
    const drawing = localDrawing;
    const setDrawing = setLocalDrawing;
    const drawingMode = localDrawingMode;
    const setDrawingMode = setLocalDrawingMode;

    // Helper function to get drawing type from ol geometry type
    const getDrawingTypeFromGeometryType = (geometryType: string): string => {
      if (geometryType.includes("Point")) return "Point";
      if (geometryType.includes("LineString")) return "Line";
      if (geometryType.includes("Polygon")) return "Polygon";
      return "Unknown";
    };

    // React to clearing trigger from Zustand store
    useEffect(() => {
      if (!shouldClearDrawing || !map) return;

      // Skip clearing if this instance is excluded
      if (excludeInstanceId && excludeInstanceId === instanceId) {
        resetClearDrawing();
        return;
      }

      // Clear all features from the drawing layer (not feature layers)
      const vectorLayer = map
        .getLayers()
        .getArray()
        .find((layer) => {
          return (
            layer instanceof VectorLayer &&
            layer.get("name") === "drawing-layer"
          );
        }) as VectorLayer<any> | undefined;

      if (vectorLayer) {
        const source = vectorLayer.getSource();
        source?.clear();
      }

      // Reset geometry states for ALL instances (not just when drawing)
      setGeometryCaptured(false);
      setGeometryLoaded(false);
      setLoadedGeometryType("");
      setCapturedGeometryType("");

      // Stop drawing if active
      if (drawing) {
        map.getInteractions().forEach((interaction) => {
          if (!interaction) return; // Skip if undefined/null

          const name = interaction.getProperties()?.name;
          if (
            interaction instanceof Modify ||
            interaction instanceof Snap ||
            (interaction instanceof Draw &&
              name === DrawingModes[drawingMode].toString())
          ) {
            interaction.setActive(false); // Deactivate instead of removing
          }
        });
        setDrawing(false);
        onDrawingStop?.();
      }

      // Reset the trigger
      resetClearDrawing();
    }, [
      shouldClearDrawing,
      excludeInstanceId,
      instanceId,
      map,
      drawing,
      drawingMode,
      onDrawingStop,
      resetClearDrawing,
    ]);

    // ESC key to stop drawing
    useEffect(() => {
      if (!drawing || !map) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          // Stop drawing interactions
          map.getInteractions().forEach((interaction) => {
            const name = interaction.getProperties().name;
            if (
              interaction instanceof Modify ||
              interaction instanceof Snap ||
              (interaction instanceof Draw &&
                name === DrawingModes[drawingMode].toString())
            ) {
              interaction.setActive(false);
            }
          });

          setDrawing(false);
          onDrawingStop?.();
        }
      };

      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }, [drawing, map, drawingMode, onDrawingStop]);

    // Effect to load initial wkt geometry for edit mode
    useEffect(() => {
      if (!map || !initialWkt) return;

      const wktFormat = new WKT();

      try {
        // Parse wkt and transform from espg3067 to epsg3857
        const feature = wktFormat.readFeature(initialWkt, {
          dataProjection: "EPSG:3067",
          featureProjection: "EPSG:3857",
        });

        // Find the vector layer
        const vectorLayer = map
          .getLayers()
          .getArray()
          .find((layer) => {
            return layer instanceof VectorLayer;
          }) as VectorLayer<any> | undefined;

        if (vectorLayer) {
          const source = vectorLayer.getSource();
          // Clear any existing features first
          source?.clear();
          // Add the loaded feature
          source?.addFeature(feature);

          // Get the geometry type from the loaded feature
          const geometryType = feature.getGeometry()?.getType() || "";
          const loadedType = getDrawingTypeFromGeometryType(geometryType);

          // Set the drawing mode to match the loaded geometry type
          if (loadedType === "Point") {
            setDrawingMode(DrawingModes.Point);
          } else if (loadedType === "Line") {
            setDrawingMode(DrawingModes.MultiLineString);
          } else if (loadedType === "Polygon") {
            setDrawingMode(DrawingModes.Polygon);
          }
          setGeometryLoaded(true);
          setGeometryCaptured(false);
          setLoadedGeometryType(loadedType);

          const extent = feature.getGeometry()?.getExtent();
          if (extent) {
            map.getView().fit(extent, {
              padding: [50, 50, 50, 50],
              maxZoom: 16,
              duration: 500,
            });
          }

          console.log("Initial WKT geometry loaded:", initialWkt);
        }
      } catch (error) {
        console.error("Error loading initial WKT geometry:", error);
      }
    }, [map, initialWkt, setDrawingMode]); // Effect to capture wkt when drawing is completed
    useEffect(() => {
      if (!map || !drawing) return;

      const wktFormat = new WKT();

      // Find the active draw interaction based on current drawing mode
      const drawInteraction = map
        .getInteractions()
        .getArray()
        .find((interaction) => {
          if (interaction instanceof Draw) {
            const name = interaction.getProperties().name;
            return name === DrawingModes[drawingMode].toString();
          }
          return false;
        }) as Draw | undefined;

      if (!drawInteraction) {
        console.warn(
          "Draw interaction not found for mode:",
          DrawingModes[drawingMode],
        );
        return;
      }

      // Event handler for when drawing is completed
      const handleDrawEnd = (event: any) => {
        const feature = event.feature;
        const geometry: Geometry = feature.getGeometry();

        // Convert geometry to wkt with projection transformation, epsg3857 (web mercator) to epsg3067 (euref-fin)
        const wktString = wktFormat.writeGeometry(geometry, {
          dataProjection: "EPSG:3067",
          featureProjection: "EPSG:3857",
        });

        // Get geometry type
        const geometryType = geometry.getType();
        const drawingType = getDrawingTypeFromGeometryType(geometryType);

        // Store the captured geometry type
        setCapturedGeometryType(drawingType);

        // Clear previous geometries from the vector source (keep only new one)
        const vectorLayer = map
          .getLayers()
          .getArray()
          .find((layer) => {
            return layer instanceof VectorLayer;
          }) as VectorLayer<any> | undefined;

        if (vectorLayer) {
          const source = vectorLayer.getSource();
          // Get all features except the newly drawn one
          const allFeatures = source?.getFeatures() || [];
          const oldFeatures = allFeatures.filter((f: Feature) => f !== feature);

          // Remove only old features
          oldFeatures.forEach((f: Feature) => source?.removeFeature(f));
        }

        // Set geometry captured flag
        setGeometryCaptured(true);

        // Disable all drawing interactions after completion
        map.getInteractions().forEach((interaction) => {
          const name = interaction.getProperties().name;
          if (
            interaction instanceof Modify ||
            interaction instanceof Snap ||
            (interaction instanceof Draw &&
              name === DrawingModes[drawingMode].toString())
          ) {
            interaction.setActive(false);
          }
        });

        setDrawing(false);

        console.log("Geometry captured:", {
          wkt: wktString,
          drawingType: drawingType,
          geometryType: geometryType,
        });

        // Callback with captured wkt and drawing type
        onGeometryCaptured?.(wktString, drawingType);

        setTimeout(() => {
          onDrawingStop?.();
        }, 100);
      };

      // Attach event listener
      drawInteraction.on("drawend", handleDrawEnd);

      // Cleanup function
      return () => {
        drawInteraction.un("drawend", handleDrawEnd);
      };
    }, [
      map,
      drawing,
      drawingMode,
      onGeometryCaptured,
      onDrawingStop,
      setDrawing,
    ]);

    const handleDrawingToggle = () => {
      const newDrawingState = !drawing;

      // If starting to draw, clear OTHER instances but keep this one
      if (newDrawingState) {
        triggerClearDrawing(instanceId);
      }

      map?.getInteractions().forEach((interaction) => {
        if (!interaction) return; // Skip if undefined/null

        const name = interaction.getProperties()?.name;
        if (
          interaction instanceof Modify ||
          interaction instanceof Snap ||
          (interaction instanceof Draw &&
            name === DrawingModes[drawingMode].toString())
        ) {
          interaction.setActive(newDrawingState);
        }
      });

      setDrawing(newDrawingState);

      if (newDrawingState) {
        setGeometryCaptured(false);
        setGeometryLoaded(false);
        setLoadedGeometryType("");
        setCapturedGeometryType("");
        onDrawingStart?.();
      } else {
        onDrawingStop?.();
      }
    };

    const handleDrawingModeChange = (value: string) => {
      setDrawingMode(parseInt(value, 10) as DrawingModes);
    };

    // const handleTileLayerToggle = () => {
    //     const newLayer = tilelayer === 'osm' ? 'satellite' : 'osm';
    //     onTileLayerChange?.(newLayer);
    // };

    const getTranslatedDrawingType = (drawingType: string) => {
      switch (drawingType) {
        case "Point":
          return getTranslation("LABEL_DRAWING_TYPE_POINT") || "Point";
        case "Line":
          return getTranslation("LABEL_DRAWING_TYPE_LINE") || "Line";
        case "Polygon":
          return getTranslation("LABEL_DRAWING_TYPE_POLYGON") || "Polygon";
        default:
          return getTranslation("LABEL_DRAWING_TYPE_UNKNOWN") || "Unknown";
      }
    };

    const drawingModeLabel = useMemo(() => {
      switch (drawingMode) {
        case 0:
          return getTranslation("LABEL_DRAWING_MODE_POINT") || "Point";
        case 1:
          return getTranslation("LABEL_DRAWING_MODE_POLYGON") || "Polygon";
        case 2:
          return getTranslation("LABEL_DRAWING_MODE_LINE") || "Line";
        default:
          return getTranslation("LABEL_DRAWING_MODE_UNKNOWN") || "Unknown";
      }
    }, [drawingMode, getTranslation]);

    // Determine geometry status label
    const geometryStatusLabel = useMemo(() => {
      if (geometryCaptured) {
        return getTranslation("LABEL_DRAWING_STATUS_CAPTURED") || "Captured";
      }
      if (geometryLoaded && loadedGeometryType) {
        return `${getTranslatedDrawingType(loadedGeometryType)} ${
          getTranslation("LABEL_DRAWING_STATUS_LOADED_SUFFIX") || "Loaded"
        }`;
      }
      return (
        getTranslation("LABEL_DRAWING_STATUS_NOT_CAPTURED") || "Not Captured"
      );
    }, [geometryCaptured, geometryLoaded, loadedGeometryType, getTranslation]);

    // Expose clearGeometry function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        clearGeometry: () => {
          // Clear all features from vector layer
          const vectorLayer = map
            ?.getLayers()
            .getArray()
            .find((layer) => {
              return layer instanceof VectorLayer;
            }) as VectorLayer<any> | undefined;

          if (vectorLayer) {
            const source = vectorLayer.getSource();
            source?.clear();
          }

          // Reset geometry states
          setGeometryCaptured(false);
          setGeometryLoaded(false);
          setLoadedGeometryType("");
          setCapturedGeometryType("");

          // Stop drawing if active
          if (drawing) {
            map?.getInteractions().forEach((interaction) => {
              if (!interaction) return; // Skip if undefined/null

              const name = interaction.getProperties()?.name;
              if (
                interaction instanceof Modify ||
                interaction instanceof Snap ||
                (interaction instanceof Draw &&
                  name === DrawingModes[drawingMode].toString())
              ) {
                interaction.setActive(false);
              }
            });
            setDrawing(false);
          }
        },
      }),
      [map, drawing, drawingMode, setDrawing],
    );

    return (
      <Box>
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <Paper withBorder p="sm" radius="md" style={{ flexShrink: 0 }}>
            <Group gap="sm" align="center" wrap="nowrap">
              {(() => {
                const startLabel = getTranslation("LABEL_DRAWING_START");
                const stopLabel = getTranslation("LABEL_DRAWING_STOP");
                const isMissing = drawing
                  ? !stopLabel || stopLabel === "LABEL_DRAWING_STOP"
                  : !startLabel || startLabel === "LABEL_DRAWING_START";
                return (
                  <Button
                    size="sm"
                    variant={drawing ? "filled" : "light"}
                    onClick={handleDrawingToggle}
                    style={{
                      minWidth: "100px",
                      fontSize: isMissing ? "10px" : undefined,
                    }}
                    disabled={disabled}
                  >
                    {drawing ? stopLabel || "Stop" : startLabel || "Start"}
                  </Button>
                );
              })()}
              <SegmentedControl
                size="sm"
                value={String(drawingMode)}
                onChange={handleDrawingModeChange}
                disabled={disabled || drawing}
                data={[
                  {
                    label: (
                      <Tooltip label={getTranslation("LABEL_POINT") || "Point"}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconPoint size={16} />
                        </div>
                      </Tooltip>
                    ),
                    value: "0",
                  },
                  {
                    label: (
                      <Tooltip
                        label={getTranslation("LABEL_POLYGON") || "Polygon"}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconPolygon size={16} />
                        </div>
                      </Tooltip>
                    ),
                    value: "1",
                  },
                  {
                    label: (
                      <Tooltip label={getTranslation("LABEL_LINE") || "Line"}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconLineDotted size={16} />
                        </div>
                      </Tooltip>
                    ),
                    value: "2",
                  },
                ]}
              />
              <Badge
                size="sm"
                variant="dot"
                color={drawing ? "red" : "gray"}
                style={{ minWidth: "75px" }}
              >
                {drawing
                  ? getTranslation("LABEL_DRAWING_ACTIVE") || "Active"
                  : getTranslation("LABEL_DRAWING_IDLE") || "Idle"}
              </Badge>
              <Badge
                size="sm"
                variant="light"
                color={geometryCaptured || geometryLoaded ? "green" : "gray"}
                style={{ minWidth: "110px" }}
              >
                {geometryStatusLabel}
              </Badge>
            </Group>
          </Paper>

          {mode === "view" && (
            <>
              {!drawing && !geometryCaptured && !disabled && (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  color="blue"
                  variant="light"
                  style={{ flex: 1 }}
                  styles={{
                    message: { textAlign: "center" },
                  }}
                >
                  <Text size="sm">
                    {geometryLoaded
                      ? getTranslation("LABEL_DRAWING_EDIT_REDRAW") ||
                        "This form allows you to edit object properties. Click start to redraw."
                      : getTranslation("LABEL_DRAWING_EDIT_DRAW") ||
                        "This form allows you to edit object properties. Click start to draw."}
                  </Text>
                </Alert>
              )}

              {disabled && !geometryCaptured && !drawing && (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  color="blue"
                  variant="light"
                  style={{ flex: 1 }}
                  styles={{
                    message: { textAlign: "center" },
                  }}
                >
                  <Text size="sm">
                    {getTranslation("LABEL_DRAWING_NO_PERMISSION") ||
                      "You do not have permission to modify the drawing."}
                  </Text>
                </Alert>
              )}

              {drawing && !geometryCaptured && (
                <Alert
                  color="blue"
                  icon={<IconInfoCircle size={16} />}
                  variant="light"
                  style={{ flex: 1 }}
                  styles={{
                    message: { textAlign: "center" },
                  }}
                >
                  <Text size="sm">
                    {getTranslation("LABEL_DRAWING_INSTRUCTION") ||
                      `Draw a ${drawingModeLabel.toLowerCase()} on the map.`}
                    {drawingMode === 0 &&
                      (getTranslation("LABEL_DRAWING_INSTRUCTION_POINT") ||
                        "Draw a point on the map. Click to place the point.")}
                    {drawingMode === 1 &&
                      (getTranslation("LABEL_DRAWING_INSTRUCTION_POLYGON") ||
                        " Double-click to finish the polygon.")}
                    {drawingMode === 2 &&
                      (getTranslation("LABEL_DRAWING_INSTRUCTION_LINE") ||
                        " Double-click to finish the line.")}
                  </Text>
                </Alert>
              )}

              {geometryCaptured && (
                <Alert
                  icon={<IconCheck size={16} />}
                  color="green"
                  variant="light"
                  style={{ flex: 1 }}
                  styles={{
                    message: { textAlign: "center" },
                  }}
                >
                  <Text size="sm">
                    {getTranslatedDrawingType(capturedGeometryType)}{" "}
                    {getTranslation("LABEL_DRAWING_CAPTURED_SUCCESS_SUFFIX") ||
                      "geometry has been captured successfully."}
                  </Text>
                </Alert>
              )}
            </>
          )}
        </Group>

        {mode === "create" && (
          <>
            {!drawing && !geometryCaptured && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                color="blue"
                variant="light"
                mt="sm"
                styles={{
                  message: { textAlign: "center" },
                }}
              >
                <Text size="sm">
                  {getTranslation("LABEL_DRAWING_CREATE_DRAW") ||
                    "This form allows you to create a new object. Click start to draw."}
                </Text>
              </Alert>
            )}

            {drawing && !geometryCaptured && (
              <Alert
                color="blue"
                icon={<IconInfoCircle size={16} />}
                variant="light"
                mt="sm"
                styles={{
                  message: { textAlign: "center" },
                }}
              >
                <Text size="sm">
                  {drawingMode === 0 &&
                    (getTranslation("LABEL_DRAWING_INSTRUCTION_POINT") ||
                      "Draw a point on the map. Click to place the point.")}
                  {drawingMode === 1 &&
                    (getTranslation("LABEL_DRAWING_INSTRUCTION_POLYGON") ||
                      "Draw a polygon on the map. Double-click to finish the polygon.")}
                  {drawingMode === 2 &&
                    (getTranslation("LABEL_DRAWING_INSTRUCTION_LINE") ||
                      "Draw a line on the map. Double-click to finish the line.")}
                </Text>
              </Alert>
            )}

            {geometryCaptured && (
              <Alert
                icon={<IconCheck size={16} />}
                color="green"
                variant="light"
                mt="sm"
                styles={{
                  message: { textAlign: "center" },
                }}
              >
                <Text size="sm">
                  {getTranslatedDrawingType(capturedGeometryType)}{" "}
                  {getTranslation("LABEL_DRAWING_CAPTURED_SUCCESS_SUFFIX") ||
                    "geometry has been captured successfully."}
                </Text>
              </Alert>
            )}
          </>
        )}
      </Box>
    );
  },
);

DrawingControls.displayName = "DrawingControls";

export default DrawingControls;
